import { PartyPlates } from "./party.ts";
import { DEFAULT_NAMEPLATE_CHOICES, type NameplateChoices } from "../../settings.ts";

/**
 * What the nameplates tell the game report (P1.9-T16), the switch a measurement turns them off with, and the
 * viewer's Nameplates choices they draw (P1.9-T24): the pool's counts after each frame, and on request every plate
 * held, as a browser scenario reads them.
 */

/**
 * One plate as the report lists it: the entity that holds it, the text it draws (the first name, the full name, or
 * nothing beside a mark alone), whether it is drawn, its flag mark's atlas frame, its name's tint, where its parts
 * stand in logical pixels at the UI scale it was laid out at, its width in logical pixels, how many display objects
 * it is, the staff badge it draws (0 for none) and how (`glyph`, the icon key of the art, or empty for none), its idle and AFK mark (ADR-090 decision 8: the z's frame, `blank` on the loop's empty step, empty when none
 * draws; whether the word AFK draws; where the z's frame stands), and whether they sit in the UI layer, which the UI
 * camera draws over the whole world.
 */
export interface PlateReading {
  readonly handle: number;
  readonly text: string;
  readonly shown: boolean;
  readonly mark: string;
  readonly tint: number;
  readonly textX: number;
  readonly textY: number;
  readonly markX: number;
  readonly markY: number;
  readonly scale: number;
  readonly width: number;
  readonly badge: number;
  readonly badgeDrawn: string;
  readonly objects: number;
  readonly idleMark: string;
  readonly idleWord: boolean;
  readonly idleX: number;
  readonly idleY: number;
  readonly party: boolean;
  readonly partyX: number;
  readonly partyY: number;
  readonly partyTint: number;
  readonly inUiLayer: boolean;
}

/**
 * The nameplates' report, switch and choices. `enabled` false hides every plate and returns it to the pool, for a
 * measurement of the frame without them. `others` and `own` are the viewer's Nameplates choices, which the page
 * sets from its settings as it loads and as Settings changes them (`NameplateSettings`), and which the plates follow
 * on the next frame. `active`, `free`, `created` and `shown` are the pool's plates held, waiting, made so far and
 * drawn, after the last frame; `texts`, `marks` and `layouts` count every text set on a plate, every mark chosen for
 * one and every time one is laid out, which grow only when a name, a choice, a flag byte, an anchor or the UI scale
 * changes; `list` reads every plate held. `idleMarks` is the viewer's Show idle and AFK marks choice (P1.9-T45,
 * `IdleMarksSetting`), read each frame, and `idleShown` how many marks drew in the last frame. `party` holds the seated members' names (P2.13-T6), set by the page from its roster, which the plates read to draw the party mark.
 */
export interface NameplateReport extends NameplateChoices {
  enabled: boolean;
  idleMarks: boolean;
  idleShown: number;
  party: PartyPlates;
  active: number;
  free: number;
  created: number;
  shown: number;
  texts: number;
  marks: number;
  layouts: number;
  list: () => readonly PlateReading[];
  /**
   * The handle of the other character whose drawn plate holds a point of the canvas, in device pixels, or -1: the
   * player menu opens on a plate as it does on a body.
   */
  plateAt: (x: number, y: number) => number;
}

/**
 * A report with nothing drawn yet, holding the default choices until the page sets the viewer's.
 *
 * @returns The report, its list empty until the nameplates are created.
 */
export function createNameplateReport(): NameplateReport {
  return { enabled: true, idleMarks: true, idleShown: 0, party: new PartyPlates(), ...DEFAULT_NAMEPLATE_CHOICES, active: 0, free: 0, created: 0, shown: 0, texts: 0, marks: 0, layouts: 0, list: () => [], plateAt: () => -1 };
}
